import { useCallback, useEffect, useRef, useState } from "react";
import {
  RetrospectiveSummaryDTO,
  RetrospectiveListResponse,
} from "@retrospecter/shared/types";
import { apiGet } from "../api/client";

export type RetroFilter = "ALL" | "ACTIVE" | "COMPLETED" | "DRAFT";

interface RetroListState {
  retrospectives: RetrospectiveSummaryDTO[];
  /** True only before the first result — never while refreshing a shown list. */
  loading: boolean;
  refreshing: boolean;
  error?: string;
}

export function useRetroList(filter: RetroFilter = "ALL") {
  const [state, setState] = useState<RetroListState>({
    retrospectives: [],
    loading: true,
    refreshing: false,
  });
  const loaded = useRef(false);

  const load = useCallback(async () => {
    // Keep the rows that are already on screen; swapping them for skeletons on
    // every refresh reads as the page flashing.
    setState((prev) => ({
      ...prev,
      loading: !loaded.current,
      refreshing: loaded.current,
      error: undefined,
    }));

    try {
      const query = filter === "ALL" ? "" : `?status=${filter}`;
      const data: RetrospectiveListResponse = await apiGet(
        `/api/retrospectives${query}`
      );
      loaded.current = true;
      setState({
        retrospectives: data.retrospectives,
        loading: false,
        refreshing: false,
      });
    } catch (error) {
      setState({
        retrospectives: [],
        loading: false,
        refreshing: false,
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  return { ...state, refetch: load };
}
